/**
 * CoachPromptSettingsPanel — split-view editor with Test modal
 *
 * Left column : section list (12 sections, grouped). Custom/dirty dots.
 * Right column: selected section editor.
 *               Platform default always visible as a reference block.
 *               Custom override textarea below.
 * Test modal  : paste or upload a transcript, run a live preview
 *               using the current draft settings (before saving).
 * Save footer : appears when any field is dirty.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { config } from '../config/environment';
import { endpoints } from '../config/endpoints';

// ── Types ──────────────────────────────────────────────────────────────────────

interface PromptSettings {
  prompt_session_overview:   string | null;
  prompt_wins:               string | null;
  prompt_agreement:          string | null;
  prompt_key_topics:         string | null;
  prompt_ef_patterns:        string | null;
  prompt_insights:           string | null;
  prompt_coaching_approach:  string | null;
  prompt_client_strengths:   string | null;
  prompt_progress:           string | null;
  prompt_challenges:         string | null;
  prompt_ef_themes:          string | null;
  prompt_action_items:       string | null;
  prompt_coach_notes:        string | null;
  prompt_nudge_message:      string | null;
  prompt_tracker:            string | null;
  prompt_session_prep:       string | null;
  current_version:           number;
  sections_config:           Record<string, unknown> | null;
}

type PromptField = keyof Omit<PromptSettings, 'current_version' | 'sections_config'>;

interface HistoryItem {
  version_number:    number;
  changed_at:        string;
  change_note:       string | null;
  settings_snapshot: Record<string, string | null>;
}

interface PreviewResult {
  summary_text: string;
  ef_themes:    string[];
  action_items: { title: string; assigned_to: string; due_date: string | null; nudge_context: string }[];
  coach_notes:  string;
}

interface CustomSection {
  id: string;           // temp client-side id before save, real uuid after
  display_name: string;
  instructions: string;
  enabled: boolean;
  client_visible: boolean;
}

interface SectionsConfig {
  disabled_built_in: string[];
  client_hidden: string[];
  custom: CustomSection[];
}

// ── Section definitions ────────────────────────────────────────────────────────

interface FieldDef { field: PromptField; label: string; hint: string; detail: string }
interface GroupDef  { key: string; title: string; fields: FieldDef[] }

const SECTION_GROUPS: GroupDef[] = [
  {
    key: 'summary',
    title: 'Session Summary',
    fields: [
      {
        field: 'prompt_session_overview',
        label: 'Session Overview',
        hint: 'Opening snapshot of the session.',
        detail: 'This is the first thing you\'ll read in your notes. It captures what the client brought to the session and where things landed by the end. By default it\'s kept to 2 sentences — enough context without repetition.',
      },
      {
        field: 'prompt_wins',
        label: 'Wins',
        hint: 'Client-reported wins and positive momentum.',
        detail: 'Captures wins and achievements explicitly mentioned by the client during the session. Only grounded in what the client directly said — no inferred wins. Adjust bullet count or how "no wins" is handled.',
      },
      {
        field: 'prompt_agreement',
        label: 'Session Agreement',
        hint: 'The explicit focus, measure, and desired outcome for this session.',
        detail: 'Records the coaching agreement: what the coach and client agreed to work on, how progress would be measured, and what the client said would make the session feel worthwhile. Each sub-item is filled from the transcript or marked "Not discussed."',
      },
      {
        field: 'prompt_key_topics',
        label: 'Key Topics Discussed',
        hint: 'Main themes covered during the session.',
        detail: 'A short bulleted list of the primary subjects, challenges, or areas the conversation touched on. Fine-tune this if you want fewer bullets for brevity, or more detail on specific topics.',
      },
      {
        field: 'prompt_ef_patterns',
        label: 'EF / ADHD Patterns Observed',
        hint: 'Executive functioning patterns that showed up.',
        detail: 'This section identifies specific executive functioning (EF) or ADHD-related patterns observed in the transcript — things like task avoidance, time blindness, or emotional dysregulation. Each pattern is grounded in a specific transcript moment. You can adjust how many patterns are noted or whether framework labels (ICF/PAAC) are included.',
      },
      {
        field: 'prompt_insights',
        label: 'Client Insights & Breakthroughs',
        hint: 'Moments of clarity or perspective shift.',
        detail: 'Captures genuine "aha" moments — when the client named something new, reframed a belief, or had a meaningful realization. These are often the most useful notes to revisit before the next session.',
      },
      {
        field: 'prompt_coaching_approach',
        label: 'Coaching Approach Used',
        hint: 'Techniques and strategies you applied.',
        detail: 'A record of the coaching techniques used in this session — useful for your own reflection and for tracking which approaches work best with a particular client. You can adjust this to include or exclude framework references.',
      },
      {
        field: 'prompt_client_strengths',
        label: 'Client Strengths Demonstrated',
        hint: 'Strengths the client showed during the session.',
        detail: 'Grounded in what the client actually said or did — not inferred. Useful for strengths-based coaching and for sharing encouragement with clients. Each strength is anchored to a specific transcript moment.',
      },
      {
        field: 'prompt_challenges',
        label: 'Current Challenges & Obstacles',
        hint: 'Blockers or difficulties the client is facing.',
        detail: 'Only challenges explicitly named or demonstrated in the session — not guesses about the future. Adjust this if you\'d like to allow reasonable clinical inferences to be included.',
      },
    ],
  },
  {
    key: 'outputs',
    title: 'Other Outputs',
    fields: [
      {
        field: 'prompt_ef_themes',
        label: 'EF Themes',
        hint: 'Which of the 8 EF domains were active.',
        detail: 'The 8 canonical EF domains (task initiation, working memory, time management, emotional regulation, organization, prioritization, sustained attention, cognitive flexibility) are always the vocabulary used here — this cannot be changed. You can adjust how many themes are listed or how prominently each must appear to qualify.',
      },
      {
        field: 'prompt_action_items',
        label: 'Action Items',
        hint: 'Concrete next steps with due dates.',
        detail: 'Action items are extracted from the transcript and must start with an action verb and name a specific outcome. Dates are inferred as aggressively as possible from the conversation. Each item includes a warm, ADHD-friendly nudge message used when sending reminders. You can adjust the tone of nudges, the aggressiveness of date inference, or the maximum number of items.',
      },
      {
        field: 'prompt_coach_notes',
        label: 'Coach Notes',
        hint: 'Private observations — visible to you only.',
        detail: 'These notes are for you, not the client. They capture what worked well, patterns worth watching across sessions, ICF competency observations, and a recommended focus for the next session. You can turn individual sub-components on or off to match your documentation style.',
      },
      {
        field: 'prompt_tracker',
        label: 'Check-in Questions',
        hint: 'Instructions for generating weekly check-in questions.',
        detail: 'Controls how Claude generates check-in questions when you click "Generate from Session Notes" in the Check-in builder. The session notes are always passed as context — this setting controls the tone, number of questions, and question type preferences. Adjust this to match your coaching voice or to focus on specific question formats.',
      },
    ],
  },
  {
    key: 'nudge',
    title: 'Nudge Messages',
    fields: [
      {
        field: 'prompt_nudge_message',
        label: 'Nudge Message Template',
        hint: 'Template for action item reminder messages.',
        detail: 'When you send a nudge, this template controls how the reminder is written. The placeholders {client_name}, {action_item_title}, and {nudge_context} are filled in automatically. You can adjust the tone — warmer, more motivational, more brief — to match your coaching voice.',
      },
    ],
  },
  {
    key: 'session_prep',
    title: 'Session Prep',
    fields: [
      {
        field: 'prompt_session_prep',
        label: 'Session Prep Focus',
        hint: 'Custom focus instruction for pre-session briefings.',
        detail: 'When generating a pre-session briefing, this instruction is added to the AI prompt so it can incorporate your preferred focus. For example: "Always flag emotional regulation patterns when present" or "Note the client\'s ADHD diagnosis context when relevant." Leave blank to use the default briefing format. Session prep notes are always private and never visible to clients.',
      },
    ],
  },
];

const ALL_FIELDS: PromptField[] = SECTION_GROUPS.flatMap(g => g.fields.map(f => f.field));
const ALL_FIELD_DEFS: FieldDef[] = SECTION_GROUPS.flatMap(g => g.fields);

// The 8 summary section keys that can be toggled on/off.
// Maps backend key → PromptField so the sidebar can link toggle state to the editor.
const SUMMARY_BUILT_IN_KEYS: { key: string; field: PromptField }[] = [
  { key: 'session_overview',  field: 'prompt_session_overview' },
  { key: 'wins',              field: 'prompt_wins' },
  { key: 'agreement',         field: 'prompt_agreement' },
  { key: 'key_topics',        field: 'prompt_key_topics' },
  { key: 'ef_patterns',       field: 'prompt_ef_patterns' },
  { key: 'insights',          field: 'prompt_insights' },
  { key: 'coaching_approach', field: 'prompt_coaching_approach' },
  { key: 'client_strengths',  field: 'prompt_client_strengths' },
  { key: 'progress',          field: 'prompt_progress' },
  { key: 'challenges',        field: 'prompt_challenges' },
];

const MANDATORY_BUILT_IN = new Set(['session_overview']);

// Default client visibility: only Session Overview, Wins, and Session Agreement are visible.
// All other built-in sections are hidden from clients unless the coach explicitly enables them.
const DEFAULT_CLIENT_HIDDEN = [
  'key_topics', 'ef_patterns', 'insights', 'coaching_approach',
  'client_strengths', 'progress', 'challenges',
];

const EMPTY_SECTIONS_CONFIG: SectionsConfig = { disabled_built_in: [], client_hidden: DEFAULT_CLIENT_HIDDEN, custom: [] };

let _tempIdCounter = 0;
function tempId(): string { return `_tmp_${++_tempIdCounter}`; }

// ── Helpers ────────────────────────────────────────────────────────────────────

function authHeader(): Record<string, string> {
  const token = localStorage.getItem('authToken') || '';
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

function shortKey(field: PromptField): string {
  return field.replace('prompt_', '');
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function _parseSectionsConfig(raw: any): SectionsConfig {
  if (!raw) return { ...EMPTY_SECTIONS_CONFIG };
  return {
    disabled_built_in: Array.isArray(raw.disabled_built_in) ? raw.disabled_built_in : [],
    client_hidden: Array.isArray(raw.client_hidden) ? raw.client_hidden : [...DEFAULT_CLIENT_HIDDEN],
    custom: Array.isArray(raw.custom)
      ? raw.custom.map((c: any) => ({
          id: c.id || tempId(),
          display_name: c.display_name || '',
          instructions: c.instructions || '',
          enabled: c.enabled !== false,
          client_visible: c.client_visible === true,
        }))
      : [],
  };
}

// ── Component ──────────────────────────────────────────────────────────────────

const CoachPromptSettingsPanel: React.FC = () => {
  const [settings, setSettings]           = useState<PromptSettings | null>(null);
  const [defaults, setDefaults]           = useState<Record<string, string>>({});
  const [draft, setDraft]                 = useState<Record<PromptField, string>>({} as Record<PromptField, string>);
  const [selected, setSelected]           = useState<string>('prompt_session_overview');
  const [sectionsConfig, setSectionsConfig]         = useState<SectionsConfig>(EMPTY_SECTIONS_CONFIG);
  const [savedSectionsConfig, setSavedSectionsConfig] = useState<SectionsConfig>(EMPTY_SECTIONS_CONFIG);
  const [addingCustom, setAddingCustom]   = useState(false);
  const [newCustomName, setNewCustomName] = useState('');
  const [newCustomInstructions, setNewCustomInstructions] = useState('');
  const [history, setHistory]             = useState<HistoryItem[]>([]);
  const [historyOpen, setHistoryOpen]     = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [loading, setLoading]             = useState(true);
  const [saving, setSaving]               = useState(false);
  const [reverting, setReverting]         = useState<number | null>(null);
  const [saveSuccess, setSaveSuccess]     = useState(false);
  const [saveError, setSaveError]         = useState<string | null>(null);
  const [changeNote, setChangeNote]       = useState('');
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);

  // Test modal
  const [testOpen, setTestOpen]           = useState(false);
  const [testTranscript, setTestTranscript] = useState('');
  const [testLoading, setTestLoading]     = useState(false);
  const [testResult, setTestResult]       = useState<PreviewResult | null>(null);
  const [testError, setTestError]         = useState<string | null>(null);
  const [copiedField, setCopiedField]     = useState<'notes' | 'coach_notes' | 'all' | null>(null);
  const fileInputRef                      = useRef<HTMLInputElement>(null);
  const successTimer                      = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const copyToClipboard = (text: string, field: 'notes' | 'coach_notes' | 'all') => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  const buildCopyAll = (r: PreviewResult): string => {
    const parts: string[] = [];
    if (r.summary_text) parts.push(`SESSION NOTES\n\n${r.summary_text}`);
    if (r.ef_themes.length > 0) parts.push(`EF THEMES\n\n${r.ef_themes.join(', ')}`);
    if (r.action_items.length > 0) {
      const items = r.action_items.map((item, i) => {
        let line = `${i + 1}. ${item.title}`;
        if (item.due_date) line += ` (Due: ${item.due_date})`;
        if (item.nudge_context) line += `\n   "${item.nudge_context}"`;
        return line;
      }).join('\n');
      parts.push(`ACTION ITEMS\n\n${items}`);
    }
    if (r.coach_notes) parts.push(`COACH NOTES\n\n${r.coach_notes}`);
    return parts.join('\n\n---\n\n');
  };

  // ── Load ───────────────────────────────────────────────────────────────────

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, dRes] = await Promise.all([
        fetch(`${config.apiBaseUrl}${endpoints.main.coaches.promptSettings}`,        { headers: authHeader() }),
        fetch(`${config.apiBaseUrl}${endpoints.main.coaches.promptSettingsDefaults}`, { headers: authHeader() }),
      ]);
      if (!sRes.ok || !dRes.ok) throw new Error();
      const s: PromptSettings        = await sRes.json();
      const d: Record<string, string> = await dRes.json();
      setSettings(s);
      setDefaults(d);
      const init = {} as Record<PromptField, string>;
      for (const f of ALL_FIELDS) init[f] = s[f] ?? '';
      setDraft(init);
      const sc = _parseSectionsConfig(s.sections_config ?? null);
      setSectionsConfig(sc);
      setSavedSectionsConfig(sc);
    } catch {
      setSaveError('Failed to load settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
    return () => { if (successTimer.current) clearTimeout(successTimer.current); };
  }, [loadSettings]);

  // ── Dirty detection ────────────────────────────────────────────────────────

  const isSectionsConfigDirty = JSON.stringify(sectionsConfig) !== JSON.stringify(savedSectionsConfig);
  const isDirty = (settings
    ? ALL_FIELDS.some(f => draft[f] !== (settings[f] ?? ''))
    : false) || isSectionsConfigDirty;

  const isFieldDirty = (f: PromptField) =>
    settings ? draft[f] !== (settings[f] ?? '') : false;

  // ── Save ───────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const body: Record<string, any> = { change_note: changeNote || null };
      for (const f of ALL_FIELDS) body[f] = draft[f].trim() || null;
      // Strip client-side temp ids before sending; server assigns real ids
      body.sections_config = {
        disabled_built_in: sectionsConfig.disabled_built_in,
        client_hidden: sectionsConfig.client_hidden,
        custom: sectionsConfig.custom.map(({ id, ...rest }) => ({
          ...rest,
          id: id.startsWith('_tmp_') ? undefined : id,
        })),
      };
      const res = await fetch(`${config.apiBaseUrl}${endpoints.main.coaches.promptSettings}`, {
        method: 'PUT', headers: authHeader(), body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error();
      const updated: PromptSettings = await res.json();
      setSettings(updated);
      const newDraft = {} as Record<PromptField, string>;
      for (const f of ALL_FIELDS) newDraft[f] = updated[f] ?? '';
      setDraft(newDraft);
      const newSc = _parseSectionsConfig(updated.sections_config ?? null);
      setSectionsConfig(newSc);
      setSavedSectionsConfig(newSc);
      setChangeNote('');
      setSaveSuccess(true);
      successTimer.current = setTimeout(() => setSaveSuccess(false), 3000);
      if (historyOpen) loadHistory();
    } catch {
      setSaveError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    if (!settings) return;
    const reset = {} as Record<PromptField, string>;
    for (const f of ALL_FIELDS) reset[f] = settings[f] ?? '';
    setDraft(reset);
    setSectionsConfig(savedSectionsConfig);
    setChangeNote('');
  };

  // ── History ────────────────────────────────────────────────────────────────

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await fetch(
        `${config.apiBaseUrl}${endpoints.main.coaches.promptSettingsHistory}`,
        { headers: authHeader() },
      );
      if (!res.ok) throw new Error();
      setHistory(await res.json());
    } catch { /* non-fatal */ }
    finally { setHistoryLoading(false); }
  };

  const toggleHistory = () => {
    if (!historyOpen) loadHistory();
    setHistoryOpen(v => !v);
  };

  const handleRevert = async (version: number) => {
    if (!window.confirm(`Restore version ${version}? Your current settings will be saved first.`)) return;
    setReverting(version);
    try {
      const res = await fetch(
        `${config.apiBaseUrl}${endpoints.main.coaches.promptSettingsRevert(version)}`,
        { method: 'POST', headers: authHeader() },
      );
      if (!res.ok) throw new Error();
      const updated: PromptSettings = await res.json();
      setSettings(updated);
      const newDraft = {} as Record<PromptField, string>;
      for (const f of ALL_FIELDS) newDraft[f] = updated[f] ?? '';
      setDraft(newDraft);
      loadHistory();
    } catch {
      setSaveError('Revert failed.');
    } finally {
      setReverting(null);
    }
  };

  // ── Test / Preview ─────────────────────────────────────────────────────────

  const handleTest = async () => {
    if (!testTranscript.trim()) {
      setTestError('Please paste or upload a transcript first.');
      return;
    }
    setTestLoading(true);
    setTestResult(null);
    setTestError(null);
    try {
      const draftSettings: Record<string, string | null> = {};
      for (const f of ALL_FIELDS) draftSettings[f] = draft[f].trim() || null;

      const res = await fetch(
        `${config.apiBaseUrl}${endpoints.main.coaches.promptSettingsPreview}`,
        {
          method: 'POST',
          headers: authHeader(),
          body: JSON.stringify({
            draft_settings: draftSettings,
            sections_config: {
              disabled_built_in: sectionsConfig.disabled_built_in,
              custom: sectionsConfig.custom.map(({ id, ...rest }) => ({
                ...rest,
                id: id.startsWith('_tmp_') ? undefined : id,
              })),
            },
            transcript: testTranscript,
          }),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Generation failed');
      }
      setTestResult(await res.json());
    } catch (e) {
      setTestError(e instanceof Error ? e.message : 'Generation failed. Please try again.');
    } finally {
      setTestLoading(false);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setTestTranscript(ev.target?.result as string || '');
    reader.readAsText(file);
    e.target.value = '';
  };

  // ── Section toggle + custom section handlers ───────────────────────────────

  const toggleBuiltIn = (key: string) => {
    if (MANDATORY_BUILT_IN.has(key)) return;
    setSectionsConfig(prev => {
      const disabled = prev.disabled_built_in.includes(key)
        ? prev.disabled_built_in.filter(k => k !== key)
        : [...prev.disabled_built_in, key];
      // If we're disabling the currently selected section, move to first enabled
      if (!prev.disabled_built_in.includes(key)) {
        const entry = SUMMARY_BUILT_IN_KEYS.find(e => e.key === key);
        if (entry && selected === entry.field) {
          const firstEnabled = SUMMARY_BUILT_IN_KEYS.find(e => !disabled.includes(e.key));
          if (firstEnabled) setSelected(firstEnabled.field);
        }
      }
      return { ...prev, disabled_built_in: disabled };
    });
  };

  const toggleClientHidden = (key: string) => {
    setSectionsConfig(prev => {
      const hidden = prev.client_hidden.includes(key)
        ? prev.client_hidden.filter(k => k !== key)
        : [...prev.client_hidden, key];
      return { ...prev, client_hidden: hidden };
    });
  };

  const addCustomSection = () => {
    const name = newCustomName.trim();
    if (!name) return;
    const newSection: CustomSection = {
      id: tempId(),
      display_name: name,
      instructions: newCustomInstructions.trim(),
      enabled: true,
      client_visible: false,
    };
    setSectionsConfig(prev => ({ ...prev, custom: [...prev.custom, newSection] }));
    setSelected(newSection.id);
    setNewCustomName('');
    setNewCustomInstructions('');
    setAddingCustom(false);
  };

  const removeCustomSection = (id: string) => {
    setSectionsConfig(prev => ({ ...prev, custom: prev.custom.filter(c => c.id !== id) }));
    if (selected === id) setSelected('prompt_session_overview');
  };

  const updateCustomSection = (id: string, patch: Partial<Omit<CustomSection, 'id'>>) => {
    setSectionsConfig(prev => ({
      ...prev,
      custom: prev.custom.map(c => c.id === id ? { ...c, ...patch } : c),
    }));
  };

  // ── Selected section ───────────────────────────────────────────────────────

  const selectedBuiltInDef = ALL_FIELD_DEFS.find(f => f.field === selected);
  const selectedCustom = sectionsConfig.custom.find(c => c.id === selected);
  // Legacy aliases for built-in branch
  const selectedDef  = selectedBuiltInDef!;
  const defaultText  = selectedBuiltInDef ? defaults[shortKey(selected as PromptField)] || '' : '';
  const isCustomised = selectedBuiltInDef ? (draft[selected as PromptField] || '').trim() !== '' : false;

  // ── Loading ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="bg-dark-card rounded-xl border border-dark-border p-5">
        <p className="text-sm text-gray-400">Loading Notes Generation settings…</p>
      </div>
    );
  }

  const totalCustomised = ALL_FIELDS.filter(f => (draft[f] || '').trim() !== '').length;
  const totalDisabled   = sectionsConfig.disabled_built_in.length;
  const totalCustomSections = sectionsConfig.custom.length;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
    <div className="bg-dark-card rounded-xl border border-dark-border shadow-sm flex flex-col">

      {/* ── Card header ─────────────────────────────────────────────────── */}
      <div className="px-5 py-4 border-b border-dark-border flex items-center justify-between flex-shrink-0">
        <div>
          <h2 className="font-semibold text-white">Notes Generation Engine</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            Customise how your session notes are written — or leave everything at the default and get great notes out of the box.
          </p>
        </div>
        <span className="text-xs text-gray-500 flex-shrink-0">
          {[
            totalCustomised > 0 && `${totalCustomised} customised`,
            totalDisabled > 0 && `${totalDisabled} off`,
            totalCustomSections > 0 && `${totalCustomSections} custom`,
          ].filter(Boolean).join(' · ') || 'All sections using defaults'}
        </span>
      </div>

      {/* ── How it works (collapsible) ───────────────────────────────────── */}
      <div className="border-b border-dark-border flex-shrink-0">
        <button
          onClick={() => setHowItWorksOpen(v => !v)}
          className="w-full flex items-center justify-between px-5 py-3 hover:bg-dark-hover transition-colors text-left"
        >
          <span className="text-xs font-medium text-primary-400">How does this work?</span>
          <svg
            className={`w-3.5 h-3.5 text-gray-500 transition-transform ${howItWorksOpen ? 'rotate-180' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {howItWorksOpen && (
          <div className="px-5 pb-5 space-y-3 bg-dark-canvas/30">
            <div className="space-y-2.5 text-xs text-gray-400 leading-relaxed pt-1">
              <p>
                <span className="text-white font-medium">The Notes Generation Engine</span> reads the transcript from each recorded session and automatically writes structured notes — a summary, action items, EF themes, and private coach notes.
              </p>
              <p>
                Every section has a <span className="text-white">built-in default</span> that works well for most coaching styles. <span className="text-white">You don't need to change anything</span> to get useful notes.
              </p>
              <p>
                If you want a section written differently — shorter, more detailed, with a different focus — you can write a custom instruction for just that section. All other sections continue using the default. There is no limit to how many sections you can customise.
              </p>
              <p>
                <span className="text-white font-medium">Important:</span> changes only affect <span className="text-white">future sessions</span>. Past notes are never modified.
              </p>
              <p>
                Use the <span className="text-white font-medium">Test</span> button below to paste a transcript and see a live preview of your notes before saving anything.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* ── Split view ──────────────────────────────────────────────────── */}
      <div className="flex min-h-0" style={{ minHeight: 480 }}>

        {/* Left: section list */}
        <div className="w-56 flex-shrink-0 border-r border-dark-border overflow-y-auto">
          {SECTION_GROUPS.map((group, gi) => {
            const isSummaryGroup = group.key === 'summary';
            return (
              <div key={group.key}>
                <div className={`px-3 py-2 ${gi > 0 ? 'border-t border-dark-border' : ''}`}>
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-500">
                    {group.title}
                  </span>
                </div>
                {group.fields.map(({ field, label }) => {
                  const active     = selected === field;
                  const customised = (draft[field] || '').trim() !== '';
                  const dirty      = isFieldDirty(field);
                  const builtInKey = isSummaryGroup
                    ? (SUMMARY_BUILT_IN_KEYS.find(e => e.field === field)?.key ?? null)
                    : null;
                  const isDisabled = builtInKey !== null && sectionsConfig.disabled_built_in.includes(builtInKey);
                  const isMandatory = builtInKey !== null && MANDATORY_BUILT_IN.has(builtInKey);
                  const isClientHidden = builtInKey !== null && sectionsConfig.client_hidden.includes(builtInKey);

                  return (
                    <div
                      key={field}
                      className={`flex items-center gap-1 transition-colors
                        ${active && !isDisabled ? 'bg-primary-900/40' : ''}
                        ${isDisabled ? 'opacity-40' : 'hover:bg-dark-hover'}`}
                    >
                      {/* Enable/disable toggle */}
                      {isSummaryGroup && builtInKey && (
                        <button
                          onClick={() => !isMandatory && toggleBuiltIn(builtInKey)}
                          className={`ml-2 flex-shrink-0 w-4 h-4 rounded flex items-center justify-center transition-colors
                            ${isMandatory ? 'cursor-default' : 'cursor-pointer'}
                            ${isDisabled
                              ? 'border border-gray-600 text-gray-600'
                              : 'bg-green-500/20 border border-green-500/60 text-green-400'
                            }`}
                          title={isMandatory ? 'Session Overview is always generated' : isDisabled ? 'Enable this section' : 'Disable this section'}
                        >
                          {isDisabled ? (
                            <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          ) : (
                            <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </button>
                      )}

                      <button
                        disabled={isDisabled}
                        onClick={() => !isDisabled && setSelected(field)}
                        className={`flex-1 text-left px-2 py-2.5 flex items-center justify-between gap-2
                          ${active && !isDisabled ? 'text-white' : 'text-gray-400'}
                          ${isDisabled ? 'cursor-default' : ''}`}
                      >
                        <span className="text-xs leading-snug">{label}</span>
                        <span className="flex-shrink-0 flex items-center gap-1">
                          {dirty && (
                            <span className="w-1.5 h-1.5 rounded-full border border-amber-400" title="Unsaved change" />
                          )}
                          {customised && !dirty && (
                            <span className="w-1.5 h-1.5 rounded-full bg-primary-400" title="Customised" />
                          )}
                        </span>
                      </button>

                      {/* Client visibility — eye toggle */}
                      {isSummaryGroup && builtInKey && !isDisabled && (
                        isMandatory ? (
                          <span
                            className="pr-2 flex-shrink-0 text-sky-400/50"
                            title="Always visible to clients"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </span>
                        ) : (
                          <button
                            onClick={() => toggleClientHidden(builtInKey)}
                            className={`pr-2 flex-shrink-0 transition-colors ${isClientHidden ? 'text-gray-500 hover:text-gray-400' : 'text-sky-400 hover:text-sky-300'}`}
                            title={isClientHidden ? 'Hidden from client — click to show' : 'Visible to client — click to hide'}
                          >
                            {isClientHidden ? (
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                              </svg>
                            ) : (
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            )}
                          </button>
                        )
                      )}

                      {/* Coach-only lock for coach_notes and ef_themes */}
                      {(field === 'prompt_coach_notes' || field === 'prompt_ef_themes') && (
                        <span className="pr-2 flex-shrink-0 text-amber-500/50" title="Always private — visible to coach only">
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}

          {/* Custom Sections group */}
          <div className="border-t border-dark-border">
            <div className="px-3 py-2 flex items-center justify-between">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-500">
                Custom Sections
              </span>
              <button
                onClick={() => { setAddingCustom(true); setNewCustomName(''); setNewCustomInstructions(''); }}
                className="text-[10px] text-primary-400 hover:text-primary-300 font-medium"
                title="Add a custom section"
              >
                + Add
              </button>
            </div>

            {sectionsConfig.custom.map(cs => (
              <div
                key={cs.id}
                className={`flex items-center gap-1 transition-colors
                  ${selected === cs.id ? 'bg-primary-900/40' : 'hover:bg-dark-hover'}`}
              >
                <button
                  onClick={() => setSelected(cs.id)}
                  className={`flex-1 text-left px-3 py-2.5 text-xs leading-snug
                    ${selected === cs.id ? 'text-white' : 'text-gray-400'}`}
                >
                  {cs.display_name || <span className="italic text-gray-600">Unnamed</span>}
                </button>
                <button
                  onClick={() => updateCustomSection(cs.id, { client_visible: !cs.client_visible })}
                  className={`flex-shrink-0 transition-colors ${cs.client_visible ? 'text-sky-400 hover:text-sky-300' : 'text-gray-500 hover:text-gray-400'}`}
                  title={cs.client_visible ? 'Visible to client — click to hide' : 'Hidden from client — click to show'}
                >
                  {cs.client_visible ? (
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  ) : (
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  )}
                </button>
                <button
                  onClick={() => removeCustomSection(cs.id)}
                  className="pr-2.5 text-gray-600 hover:text-red-400 transition-colors flex-shrink-0"
                  title="Remove section"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}

            {sectionsConfig.custom.length === 0 && !addingCustom && (
              <p className="px-3 pb-3 text-xs text-gray-600 italic">No custom sections yet.</p>
            )}

            {/* Inline add form */}
            {addingCustom && (
              <div className="px-3 pb-3 space-y-2">
                <input
                  autoFocus
                  type="text"
                  value={newCustomName}
                  onChange={e => setNewCustomName(e.target.value)}
                  placeholder="Section name"
                  className="w-full text-xs border border-dark-border rounded px-2 py-1.5
                             bg-dark-canvas text-gray-200 placeholder-gray-600 focus:ring-1 focus:ring-primary-500"
                />
                <textarea
                  value={newCustomInstructions}
                  onChange={e => setNewCustomInstructions(e.target.value)}
                  placeholder="Instructions for Claude (optional — can add later)"
                  rows={3}
                  className="w-full text-xs border border-dark-border rounded px-2 py-1.5
                             bg-dark-canvas text-gray-200 placeholder-gray-600 focus:ring-1 focus:ring-primary-500 resize-none"
                />
                <div className="flex gap-2">
                  <button
                    onClick={addCustomSection}
                    disabled={!newCustomName.trim()}
                    className="flex-1 text-xs bg-primary-600 text-white py-1 rounded
                               hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Add
                  </button>
                  <button
                    onClick={() => setAddingCustom(false)}
                    className="text-xs border border-dark-border text-gray-400 px-2 py-1 rounded hover:bg-dark-hover"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right: editor */}
        <div className="flex-1 flex flex-col min-w-0 overflow-y-auto">
          <div className="flex-1 px-5 py-4 space-y-4">

          {selectedBuiltInDef ? (
            <>
            {/* Section title + status */}
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-white">{selectedDef.label}</h3>
                <p className="text-xs text-gray-500 mt-0.5">{selectedDef.hint}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {isCustomised ? (
                  <>
                    <span className="text-xs bg-primary-900/30 text-primary-400 border border-primary-800 px-2 py-0.5 rounded-full">
                      Custom
                    </span>
                    <button
                      onClick={() => setDraft(prev => ({ ...prev, [selected]: '' }))}
                      className="text-xs text-gray-500 hover:text-gray-300 underline"
                    >
                      Reset to default
                    </button>
                  </>
                ) : (
                  <span className="text-xs text-gray-600 border border-dark-border px-2 py-0.5 rounded-full">
                    Using default
                  </span>
                )}
              </div>
            </div>

            {/* About this section */}
            <div className="bg-dark-canvas/40 border border-dark-border/60 rounded-lg px-3 py-2.5">
              <p className="text-xs text-gray-400 leading-relaxed">{selectedDef.detail}</p>
            </div>

            {/* Platform default — always visible reference block */}
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-500 mb-1.5">
                Platform default
              </p>
              <div className="rounded-lg border border-dark-border bg-dark-canvas/50 px-3 py-3
                              text-xs text-gray-400 leading-relaxed whitespace-pre-wrap select-text">
                {defaultText || <span className="italic text-gray-600">No default defined.</span>}
              </div>
            </div>

            {/* Custom override */}
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-500 mb-1.5">
                Your custom instruction{' '}
                <span className="normal-case tracking-normal font-normal text-gray-600">
                  — leave blank to use the platform default above
                </span>
              </p>
              <textarea
                value={draft[selected as PromptField]}
                onChange={e => setDraft(prev => ({ ...prev, [selected]: e.target.value }))}
                placeholder="Type your custom instruction here…"
                rows={7}
                className="w-full text-sm border border-dark-border rounded-lg p-3
                           focus:ring-2 focus:ring-primary-500 bg-dark-canvas text-gray-200
                           placeholder-gray-600 resize-y leading-relaxed"
              />
            </div>
            </>
          ) : selectedCustom ? (
            <>
            {/* Custom section editor */}
            <div className="flex items-start justify-between gap-3">
              <h3 className="text-sm font-semibold text-white">Custom Section</h3>
              <button
                onClick={() => removeCustomSection(selectedCustom.id)}
                className="text-xs text-red-400 hover:text-red-300 border border-red-900/50 px-2 py-0.5 rounded flex-shrink-0"
              >
                Remove section
              </button>
            </div>

            <div>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-500 mb-1.5">
                Section name
              </p>
              <input
                type="text"
                value={selectedCustom.display_name}
                onChange={e => updateCustomSection(selectedCustom.id, { display_name: e.target.value })}
                placeholder="e.g. Somatic Observations"
                className="w-full text-sm border border-dark-border rounded-lg p-3
                           focus:ring-2 focus:ring-primary-500 bg-dark-canvas text-gray-200
                           placeholder-gray-600"
              />
            </div>

            <div>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-500 mb-1.5">
                Instructions for Claude
              </p>
              <textarea
                value={selectedCustom.instructions}
                onChange={e => updateCustomSection(selectedCustom.id, { instructions: e.target.value })}
                placeholder="Describe what Claude should write in this section…"
                rows={8}
                className="w-full text-sm border border-dark-border rounded-lg p-3
                           focus:ring-2 focus:ring-primary-500 bg-dark-canvas text-gray-200
                           placeholder-gray-600 resize-y leading-relaxed"
              />
            </div>
            </>
          ) : (
            <p className="text-sm text-gray-500">Select a section to edit.</p>
          )}

          </div>

          {/* Version history drawer */}
          <div className="border-t border-dark-border flex-shrink-0">
            <button
              onClick={toggleHistory}
              className="w-full flex items-center justify-between px-5 py-3 hover:bg-dark-hover transition-colors text-left"
            >
              <span className="text-xs font-medium text-gray-400">
                Version History
                {settings && settings.current_version > 1
                  ? ` · ${settings.current_version - 1} saved`
                  : ''}
              </span>
              <svg
                className={`w-3.5 h-3.5 text-gray-500 transition-transform ${historyOpen ? 'rotate-180' : ''}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {historyOpen && (
              <div className="px-5 pb-4">
                {historyLoading ? (
                  <p className="text-xs text-gray-500">Loading…</p>
                ) : history.length === 0 ? (
                  <p className="text-xs text-gray-500">No history yet. Saved changes will appear here.</p>
                ) : (
                  <div className="space-y-2">
                    {history.map(item => (
                      <div key={item.version_number} className="flex items-start justify-between bg-dark-hover rounded-lg px-3 py-2.5">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-gray-300">v{item.version_number}</span>
                            <span className="text-xs text-gray-500">{formatDate(item.changed_at)}</span>
                          </div>
                          {item.change_note && <p className="text-xs text-gray-400 mt-0.5">{item.change_note}</p>}
                        </div>
                        <button
                          onClick={() => handleRevert(item.version_number)}
                          disabled={reverting === item.version_number}
                          className="ml-3 text-xs border border-dark-border text-gray-400 px-2 py-1 rounded
                                     hover:text-gray-200 hover:border-gray-500 flex-shrink-0
                                     disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {reverting === item.version_number ? 'Reverting…' : 'Revert'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Sticky footer ───────────────────────────────────────────────── */}
      <div className="border-t border-dark-border px-5 py-3 flex items-center gap-3 flex-shrink-0 bg-dark-card rounded-b-xl">
        {/* Test button — always visible */}
        <button
          onClick={() => { setTestOpen(true); setTestResult(null); setTestError(null); }}
          className="text-xs border border-dark-border text-gray-300 px-4 py-1.5 rounded-lg
                     hover:bg-dark-hover hover:text-white transition-colors flex items-center gap-1.5"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Test
        </button>

        {/* Save / Discard — only when dirty */}
        {(isDirty || saveSuccess || saveError) && (
          <>
            {saveError && <p className="text-xs text-red-400 flex-1">{saveError}</p>}
            {saveSuccess && <p className="text-xs text-green-400 flex-1">Settings saved.</p>}
            {!saveError && !saveSuccess && (
              <input
                type="text"
                value={changeNote}
                onChange={e => setChangeNote(e.target.value)}
                placeholder="Optional: describe what you changed"
                className="flex-1 text-xs border border-dark-border rounded-lg px-3 py-1.5
                           bg-dark-canvas text-gray-300 placeholder-gray-600 focus:ring-1 focus:ring-primary-500"
              />
            )}
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={handleDiscard}
                className="text-xs border border-dark-border text-gray-400 px-3 py-1.5 rounded-lg hover:bg-dark-hover"
              >
                Discard
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="text-xs bg-primary-600 text-white px-4 py-1.5 rounded-lg
                           hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>

    {/* ── Test modal ───────────────────────────────────────────────────────── */}
    {testOpen && (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
        <div className="bg-dark-card border border-dark-border rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">

          {/* Modal header */}
          <div className="px-5 py-4 border-b border-dark-border flex items-start justify-between flex-shrink-0">
            <div>
              <h3 className="font-semibold text-white">Test Your Configuration</h3>
              <p className="text-xs text-gray-400 mt-0.5">
                Paste a transcript below to see exactly what your notes will look like.
                This uses your <span className="text-white">current settings from the form</span> — including any unsaved changes.
                Nothing is saved until you hit "Save changes".
              </p>
            </div>
            <button
              onClick={() => setTestOpen(false)}
              className="text-gray-500 hover:text-gray-300 ml-4 flex-shrink-0"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {!testResult ? (
              /* Input pane */
              <div className="p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Transcript</p>
                  <div className="flex items-center gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".txt,.text"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="text-xs border border-dark-border text-gray-400 px-3 py-1 rounded-lg hover:bg-dark-hover"
                    >
                      Upload .txt file
                    </button>
                    {testTranscript && (
                      <button
                        onClick={() => setTestTranscript('')}
                        className="text-xs text-gray-500 hover:text-gray-300"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>

                <textarea
                  value={testTranscript}
                  onChange={e => setTestTranscript(e.target.value)}
                  placeholder={`Paste your session transcript here…\n\nExample:\nCoach: Hi Sarah, how has the week been?\nSarah: Honestly pretty rough. I couldn't get started on the project at all…`}
                  rows={14}
                  className="w-full text-sm border border-dark-border rounded-lg p-3
                             bg-dark-canvas text-gray-200 placeholder-gray-600
                             focus:ring-2 focus:ring-primary-500 resize-none leading-relaxed font-mono"
                />

                {testError && (
                  <p className="text-xs text-red-400">{testError}</p>
                )}

                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-600">
                    This will call the Notes Generation Engine and may take 15–30 seconds.
                  </p>
                  <button
                    onClick={handleTest}
                    disabled={testLoading || !testTranscript.trim()}
                    className="text-sm bg-primary-600 text-white px-5 py-2 rounded-lg
                               hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed
                               flex items-center gap-2"
                  >
                    {testLoading ? (
                      <>
                        <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                        </svg>
                        Generating…
                      </>
                    ) : 'Generate Preview'}
                  </button>
                </div>
              </div>
            ) : (
              /* Results pane */
              <div className="p-5 space-y-5">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-green-400 font-medium">Preview generated successfully</p>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => copyToClipboard(buildCopyAll(testResult), 'all')}
                      className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-200 transition-colors"
                    >
                      {copiedField === 'all'
                        ? <><svg className="w-3.5 h-3.5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg><span className="text-green-400">Copied</span></>
                        : <><svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>Copy all</>
                      }
                    </button>
                    <button
                      onClick={() => { setTestResult(null); setTestError(null); }}
                      className="text-xs text-gray-500 hover:text-gray-300 underline"
                    >
                      ← Try a different transcript
                    </button>
                  </div>
                </div>

                {/* Summary text */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-500">Session Notes</p>
                    <button
                      onClick={() => copyToClipboard(testResult.summary_text, 'notes')}
                      className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-300 transition-colors"
                    >
                      {copiedField === 'notes'
                        ? <><svg className="w-3 h-3 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg><span className="text-green-400">Copied</span></>
                        : <><svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>Copy</>
                      }
                    </button>
                  </div>
                  <textarea
                    readOnly
                    value={testResult.summary_text}
                    rows={12}
                    className="w-full bg-dark-canvas border border-dark-border rounded-lg px-4 py-3
                               text-xs text-gray-300 leading-relaxed resize-y font-mono focus:outline-none
                               focus:ring-1 focus:ring-dark-border cursor-text select-text"
                  />
                </div>

                {/* EF Themes */}
                {testResult.ef_themes.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-500 mb-2">EF Themes</p>
                    <div className="flex flex-wrap gap-1.5">
                      {testResult.ef_themes.map(t => (
                        <span key={t} className="text-xs bg-primary-900/30 text-primary-300 border border-primary-800 px-2 py-0.5 rounded-full capitalize">
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action Items */}
                {testResult.action_items.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-500 mb-2">Action Items</p>
                    <div className="space-y-2">
                      {testResult.action_items.map((item, i) => (
                        <div key={i} className="bg-dark-canvas border border-dark-border rounded-lg px-3 py-2.5">
                          <p className="text-xs font-medium text-white">{item.title}</p>
                          {item.due_date && (
                            <p className="text-xs text-gray-500 mt-0.5">Due: {item.due_date}</p>
                          )}
                          {item.nudge_context && (
                            <p className="text-xs text-gray-400 mt-1 italic">"{item.nudge_context}"</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Coach Notes */}
                {testResult.coach_notes && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-500">Coach Notes</p>
                      <button
                        onClick={() => copyToClipboard(testResult.coach_notes!, 'coach_notes')}
                        className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-300 transition-colors"
                      >
                        {copiedField === 'coach_notes'
                          ? <><svg className="w-3 h-3 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg><span className="text-green-400">Copied</span></>
                          : <><svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>Copy</>
                        }
                      </button>
                    </div>
                    <textarea
                      readOnly
                      value={testResult.coach_notes}
                      rows={6}
                      className="w-full bg-dark-canvas border border-dark-border rounded-lg px-4 py-3
                                 text-xs text-gray-300 leading-relaxed resize-y font-mono focus:outline-none
                                 focus:ring-1 focus:ring-dark-border cursor-text select-text"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Modal footer */}
          <div className="px-5 py-3 border-t border-dark-border flex items-center justify-between flex-shrink-0">
            <p className="text-xs text-gray-600">
              {isDirty
                ? 'You have unsaved changes — the preview reflects those changes.'
                : 'Showing your currently saved configuration.'}
            </p>
            <button
              onClick={() => setTestOpen(false)}
              className="text-xs border border-dark-border text-gray-400 px-4 py-1.5 rounded-lg hover:bg-dark-hover"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default CoachPromptSettingsPanel;
